import type * as api from '@gitbook/api-client';
import { validateAnyContentReference } from '@gitbook/app-content-refs';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { getSectionsInDoc } from '@gitbook/doc-core';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { HTTPStatusCode } from '@gitbook/util-debug';
import { assertNever } from '@gitbook/util-ts';
import { isUrl, normalizeUrl } from '@gitbook/util-url';

import type { Collection } from '../collections';
import type { Space } from '../spaces';
import { getStorageCachedURL } from '../storage';

export interface ResolvedContentReference<SpaceType = api.Space | Space> {
    /** Text to display to represent the reference */
    text: string;
    /** Target (screen or absolute url) for the reference */
    target: NavigationScreenState | string;

    /** Extra objects that have been resolved, it can be used to display extra info */
    extra?: {
        space?: SpaceType;
        page?: hiveCore.RevisionPageDocument | hiveCore.RevisionPageGroup;
        file?: hiveCore.RevisionFile;
    };
}

/**
 * A generic content reference context when a user is in a space and/or a page.
 */
interface DefaultContentReferenceContext {
    /** Organization the user is in */
    organization: string;
    /** Space the user is in */
    space?: string;
    /** Key of current page */
    page?: string;
    /** A branchId to use for revision resolution. It takes precedence over a change request branch id. */
    hiveBranch?: string;
    /** Site the user is in */
    site?: string;

    /** Reusable content id and space if this reference is coming _from_ reusable content */
    reusableContent?: {
        id: string;
        /** The space will be undefined if it's reusable content in the same space */
        space?: string;
    };
}

/**
 * A content reference context when inside a change request.
 */
interface ChangeRequestContentReferenceContext extends DefaultContentReferenceContext {
    changeRequest: string;
}

/**
 * A content reference context when inside a specific revision.
 */
interface RevisionContentReferenceContext extends DefaultContentReferenceContext {
    revision: string;
}

/**
 * The context to use when resolving a content reference.
 */
export type ContentReferenceContext =
    | DefaultContentReferenceContext
    | ChangeRequestContentReferenceContext
    | RevisionContentReferenceContext;

export type ResolveContentRefOptions<SpaceType = api.Space | Space> = {
    getUser: (user: string) => Promise<{ id: string; displayName: string } | null>;
    getSpace: (space: string) => Promise<SpaceType | null>;
    getCollection: (collection: string) => Promise<Collection | api.Collection | null>;

    hiveClient: HiveClient;

    /**
     * Resolve the text for an anchor reference.
     * If true, it will try to resolve the text of the anchor by looking at the
     * sections in the page document.
     * This is expensive as it requires fetching the page document. It defaults
     * to true but consider whether you actually need it.
     */
    resolveAnchorText?: boolean;
};

/**
 * Resolve a content reference.
 */
export async function resolveContentRef<SpaceType extends api.Space | Space = api.Space | Space>(
    contentRef: api.ContentRef,
    context: ContentReferenceContext,
    options: ResolveContentRefOptions<SpaceType>
): Promise<ResolvedContentReference<SpaceType> | null> {
    const hiveClient = options.hiveClient;

    switch (contentRef.kind) {
        case 'user': {
            const user = await options.getUser(contentRef.user);
            if (!user) {
                return null;
            }

            return {
                text: user.displayName,
                target: {
                    type: 'user',
                    user: user.id,
                },
            };
        }
        case 'space': {
            const space = await options.getSpace(contentRef.space);
            if (!space) {
                return null;
            }

            return {
                text: space.title,
                target: {
                    type: 'space',
                    organization: space.organization,
                    space: getSpaceId(space),
                    page: '',
                },
                extra: {
                    space,
                },
            };
        }
        case 'collection': {
            const collection = await options.getCollection(contentRef.collection);
            if (!collection) {
                return null;
            }

            return {
                text: collection.title,
                target: {
                    type: 'collection',
                    organization: collection.organization,
                    collection: getCollectionId(collection),
                },
            };
        }

        case 'reusable-content': {
            const spaceId = contentRef.space ?? context.space;
            if (!spaceId) {
                throw new Error('Cannot resolve a reusable content ref without a space context');
            }

            const revision = await resolveRevision(contentRef, context, options);
            if (!revision) {
                return null;
            }

            const reusableContent = revision.reusableContents[contentRef.reusableContent];
            if (!reusableContent) {
                return null;
            }

            return {
                text: reusableContent.title,
                target: {
                    type: 'space',
                    space: revision.contentId,
                    reusableContent: reusableContent.id,
                    page: '',
                },
            };
        }

        case 'openapi': {
            // @TODO to make it possible to implement we have to move this logic in another package
            // we can't access to backend-database from app-database
            return null;
        }
        case 'page': {
            const revision = await resolveRevision(contentRef, context, options);
            if (!revision) {
                return null;
            }

            const page = revision.pages[contentRef.page];
            if (!page) {
                return null;
            }

            if (page.type === hiveCore.RevisionPageType.Computed) {
                // There should be no computed pages here as we compute it in resolveRevision
                return null;
            }

            if (page.type === hiveCore.RevisionPageType.Link) {
                if (!validateAnyContentReference(page.target)) {
                    return null;
                }
                const linkResolved = await resolveContentRef(
                    page.target,
                    { space: revision.contentId, organization: context.organization },
                    options
                );
                if (!linkResolved) {
                    return null;
                }
                return {
                    text: page.title,
                    target: linkResolved.target,
                };
            }

            return {
                text: page.title,
                target: {
                    type: 'space',
                    space: revision.contentId,
                    page: hiveCore.getAbsolutePagePathInRevision(revision, page.id),
                },
                extra: {
                    page,
                },
            };
        }
        case 'anchor': {
            const spaceId = context.reusableContent?.space ?? contentRef.space ?? context.space;
            if (!spaceId) {
                throw new Error('Cannot resolve an anchor ref without a space context');
            }
            const pageId = contentRef.page || context.page;
            if (!pageId) {
                throw new Error('Cannot resolve an anchor ref without a page context');
            }

            const revision = await resolveRevision(contentRef, context, options);
            if (!revision) {
                return null;
            }

            const page = revision.pages[pageId];
            if (page?.type !== hiveCore.RevisionPageType.Document) {
                return null;
            }

            let text = contentRef.anchor;
            const resolveAnchorText = options.resolveAnchorText ?? true;
            if (resolveAnchorText) {
                const branchId =
                    'hiveBranch' in context && context.hiveBranch
                        ? context.hiveBranch
                        : spaceId === context.space && 'changeRequest' in context
                          ? getChangeRequestBranchId(context.changeRequest)
                          : hiveCore.CONTENT_DEFAULT_BRANCH;
                const document = await hiveClient.store.getBranchPageDocument(
                    spaceId,
                    branchId,
                    page
                );
                if (!document) {
                    return null;
                }

                const sections = getSectionsInDoc(document);
                const section = sections.find((s) => s.anchor === contentRef.anchor);
                if (section) {
                    if (context.page && pageId !== context.page) {
                        text = `${page.title} #${section.title}`;
                    } else {
                        text = section.title;
                    }
                }
            } else {
                text = `${page.title} #${text}`;
            }

            return {
                // Instead of showing a broken link if the section doesn't exist, we still let the user navigate to the page
                text,
                target: {
                    type: 'space',
                    space: revision.contentId,
                    page: hiveCore.getAbsolutePagePathInRevision(revision, pageId),
                    hash: contentRef.anchor,
                },
            };
        }
        case 'file': {
            const revision = await resolveFileRevision(context, options);
            if (!revision) {
                return null;
            }

            const file = revision.files[contentRef.file];
            if (!file) {
                return null;
            }

            return {
                text: file.name,
                target: getStorageCachedURL(file.downloadURL),
                extra: {
                    file,
                },
            };
        }
        case 'url': {
            // Don't resolve content refs that we don't want resolved.
            if (!isUrl(contentRef.url)) {
                return null;
            }

            return {
                text: contentRef.url,
                target: normalizeUrl(contentRef.url, {
                    // For rendering and markdown/html formatting, we preserve as much as possible the original url
                    sortQueryParameters: false,
                }),
            };
        }
        default:
            assertNever(contentRef, `Unknown content reference: ${contentRef}`);
    }
}

/**
 * Resolve an application screen to a content reference.
 */
export async function resolveScreenToContentRef(
    screen: NavigationScreenState,
    context: ContentReferenceContext,
    options: Pick<ResolveContentRefOptions, 'hiveClient' | 'getSpace' | 'getCollection'>
): Promise<api.ContentRef | null> {
    const hiveClient = options.hiveClient;

    switch (screen.type) {
        case 'space': {
            // Check permissions to access the space
            const space = await options.getSpace(screen.space);
            if (!space) {
                return null;
            }

            if (screen.reusableContent) {
                return {
                    kind: 'reusable-content',
                    space: context.space === screen.space ? undefined : screen.space,
                    reusableContent: screen.reusableContent,
                };
            }

            if (!screen.page) {
                // Root pages in spaces have an empty `page` in `screen`
                if (screen.hash) {
                    return {
                        kind: 'anchor',
                        space: screen.space !== context.space ? screen.space : undefined,
                        page: undefined,
                        anchor: screen.hash,
                    };
                }

                return {
                    kind: 'space',
                    space: screen.space,
                };
            }

            const revision = await hiveClient.store.getRevision(getSpaceId(space), space.revision);

            if (!revision) {
                return null;
            }

            const page = hiveCore.resolvePathInRevision(revision, screen.page);

            if (!page) {
                return null;
            }

            if (screen.hash) {
                return {
                    kind: 'anchor',
                    space: screen.space !== context.space ? screen.space : undefined,
                    page: page.id !== context.page ? page.id : undefined,
                    anchor: screen.hash,
                };
            }

            return {
                kind: 'page',
                space: screen.space !== context.space ? screen.space : undefined,
                page: page.id,
            };
        }
        case 'collection': {
            // Check permissions to access the collection
            const collection = await options.getCollection(screen.collection);
            if (!collection) {
                return null;
            }

            return {
                kind: 'collection',
                collection: screen.collection,
            };
        }
        case 'user': {
            return {
                kind: 'user',
                user: screen.user,
            };
        }
    }

    return null;
}

/**
 * Resolve the Hive revision from the current context.
 */
async function resolveFileRevision(
    context: ContentReferenceContext,
    options: ResolveContentRefOptions
): Promise<hiveCore.Revision | null> {
    const hiveClient = options.hiveClient;
    const contentId = await assertHiveContentId(context, options);

    if (!contentId) {
        return null;
    }

    try {
        if ('revision' in context && context.revision) {
            const revision = await hiveClient.store.getRevision(contentId, context.revision);
            return revision || null;
        }

        const revision = await hiveClient.store.getBranchRevision(
            contentId,
            'hiveBranch' in context && context.hiveBranch
                ? context.hiveBranch
                : 'changeRequest' in context && context.changeRequest
                  ? getChangeRequestBranchId(context.changeRequest)
                  : hiveCore.CONTENT_DEFAULT_BRANCH
        );

        return revision || null;
    } catch (error) {
        // Hive might throw a permission denied error if the user cannot access the content, rethrow
        // it so that it's caught further up the chain.
        if (error.statusCode === HTTPStatusCode.Forbidden) {
            throw error;
        }

        // Hive can throw errors if we try to access a branch like a space/change request before it has been initialized,
        // in this case return null.
        return null;
    }
}

/**
 * Resolve the Hive content id from the current context. Returns null if there's a valid
 * contentId but no content behind it. Throws an error if there's no valid contentId.
 */
async function assertHiveContentId(
    context: ContentReferenceContext,
    options: ResolveContentRefOptions
): Promise<string | null> {
    if (context.reusableContent?.space) {
        const space = await options.getSpace(context.reusableContent.space);
        return space ? getSpaceId(space) : null;
    }

    if (context.space) {
        const space = await options.getSpace(context.space);
        return space ? getSpaceId(space) : null;
    }

    return null;
}

async function resolveRevision(
    contentRef: api.ContentRefPage | api.ContentRefAnchor | api.ContentRefReusableContent,
    context: ContentReferenceContext,
    options: Pick<ResolveContentRefOptions, 'hiveClient' | 'getSpace'>
): Promise<hiveCore.Revision | null> {
    const currentSpaceId = context.space;
    const spaceId = context.reusableContent?.space ?? contentRef.space ?? currentSpaceId;
    if (!spaceId) {
        throw new Error('Cannot resolve a page/anchor/file ref without a space context');
    }

    // Check permissions to access the space
    const space = await options.getSpace(spaceId);
    if (!space) {
        return null;
    }

    const hiveClient = options.hiveClient;
    try {
        // If there's a revision specifically defined in the context, use that one.
        if ('revision' in context && context.revision && spaceId === currentSpaceId) {
            const sourceRevision = await hiveClient.store.getRevision(spaceId, context.revision);

            if (!sourceRevision) {
                return null;
            }

            const { revision } = await hiveClient
                .getContentComputer(spaceId)
                .getCompleteRevision(sourceRevision);
            return revision;
        }

        const revision = await hiveClient.store.getBranchRevision(
            spaceId,
            // If we're in a change request on the current space, use the head of the CR, otherwise use default content
            'hiveBranch' in context && context.hiveBranch
                ? context.hiveBranch
                : 'changeRequest' in context && context.changeRequest && spaceId === currentSpaceId
                  ? getChangeRequestBranchId(context.changeRequest)
                  : hiveCore.CONTENT_DEFAULT_BRANCH
        );

        return revision || null;
    } catch (error) {
        // Hive might throw a permission denied error if the user cannot access the content, rethrow
        // it so that it's caught further up the chain.
        if (error.statusCode === HTTPStatusCode.Forbidden) {
            throw error;
        }

        // Hive can throw errors if we try to access a branch like a space/change request before it has been initialized,
        // in this case return null.
        return null;
    }
}

/**
 * Check whether a content ref is structurally valid in a given context.
 */
export function isValidContentReferenceInContext(
    contentRef: api.ContentRef,
    context: ContentReferenceContext
): boolean {
    // Files are not valid unless they're in a space.
    if (contentRef.kind === 'file' && !context.space) {
        return false;
    }

    /**
     * Special case for content refs that are not valid (e.g. a content ref that points to a page without a space `in context of a "collection"`).
     * Ref: https://github.com/GitbookIO/support-bucket/issues/1140
     */
    if (!context.space) {
        // Relative page refs are not valid without a space
        if (contentRef.kind === 'page' && !contentRef.space) {
            return false;
        }

        // Relative anchor refs are not valid without a space
        if (contentRef.kind === 'anchor' && !contentRef.space) {
            return false;
        }
    }

    if (!context.page) {
        // Relative anchor refs are not valid without a page
        if (contentRef.kind === 'anchor' && !contentRef.page) {
            return false;
        }
    }

    return true;
}

/**
 * Get the space ID from a space-like object.
 */
export function getSpaceId(space: Space | api.Space): string {
    return 'key' in space ? space.key : space.id;
}

/**
 * Get the collection ID from a collection-like object.
 */
export function getCollectionId(collection: Collection | api.Collection): string {
    return 'key' in collection ? collection.key : collection.id;
}
